import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { basePathHelper } from '../../../setup/base-path.helper';
import { DataExportsClient, DataExportsRunPostRequest, ServiceException } from '../../../typings/generated/careerhub-client';
export interface SelectIdsParameter {
    selectAll: boolean,
    selectedIds: number[],
    deselectedIds: number[]
}

export interface FromUntilParameter {
    from?: string,
    until?: string
}

export type ParameterTypes = SelectIdsParameter | FromUntilParameter;

export type ParametersRecordType = Record<string, ParameterTypes>;

// A record where the key should be the dataSourceId
// Then another record where the key is the parameter identifier
// The object can be of different types, which will be serialised to a string and deserialised later in the backend
export type ReportParameters = Record<number, ParametersRecordType>;

export type FileResult = { url: string, fileName: string };

interface DataExportsState {
    file?: FileResult,
    isProcessing: boolean,
    errorMessage?: string,
    showDownloadModal: boolean,
    // Store parameters in a dictionary where the key is the dataSourceId
    reportParameters: ReportParameters,
    activeDataSourceId?: number
}

const initialState: DataExportsState = {
    reportParameters: {},
    isProcessing: false,
    showDownloadModal: false,
};

export interface ReportParametersSetPayloadAction {
    dataSourceId: number,
    parameterIdentifier: string,
    parameter: ParameterTypes
}

const { careerHubBaseUrl } = basePathHelper;
const client = new DataExportsClient(careerHubBaseUrl);

export const runDataExportsPost = createAsyncThunk(
    'run/data-exports',
    async (request: { id: number } & DataExportsRunPostRequest, apiThunk) => {
        try {
            const res = await client.run(
                request.id,
                {
                    parameters: request.parameters,
                    jobSeekerId: request.jobSeekerId,
                },
            );

            const url = window.URL.createObjectURL(res.result.data);
            const fileName = res.result.fileName ?? 'file.pdf';

            return { url, fileName } as FileResult;
        } catch (e: any) {
            if (e instanceof ServiceException) {
                return apiThunk.rejectWithValue(e.message);
            } else {
                return apiThunk.rejectWithValue('request failed.');
            }
        }
    },
)

export const dataExportsSlice = createSlice({
    initialState: initialState,
    name: 'data-exports',
    reducers: {
        setFileUrl(state, action: PayloadAction<FileResult>) {
            state.file = action.payload
        },
        reportParametersSet(state, action: PayloadAction<ReportParametersSetPayloadAction>) {
            const { dataSourceId, parameter, parameterIdentifier } = action.payload;

            if (dataSourceId in state.reportParameters) {
                state.reportParameters[dataSourceId]![parameterIdentifier] = parameter;
            } else {
                state.reportParameters[dataSourceId] = {
                    [parameterIdentifier]: parameter,
                }
            }
        },
        showDownloadModalSet(state, action: PayloadAction<boolean>) {
            state.showDownloadModal = action.payload;
            if (state.file) {
                URL.revokeObjectURL(state.file.url);
            }
        },
    },
    extraReducers: (builder) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        builder.addCase(runDataExportsPost.fulfilled, (state, action) => {
            // release previous file
            if (state.file) {
                URL.revokeObjectURL(state.file.url);
            }

            state.file = {
                fileName: action.payload.fileName,
                url: action.payload.url,
            };
            state.isProcessing = false;
            state.errorMessage = undefined;
            state.showDownloadModal = true;
        }),
        builder.addCase(runDataExportsPost.pending, (state) => {
            state.isProcessing = true;
        }),
        builder.addCase(runDataExportsPost.rejected, (state, action) => {
            state.isProcessing = false;
            state.errorMessage = action.payload as string;
        })
    },
});

export const {
    setFileUrl,
    reportParametersSet,
    showDownloadModalSet,
} = dataExportsSlice.actions;
